import { createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Link } from "@tanstack/solid-router";
import { Star, Trash2 } from "@fleet/icons";
import { cn, isMoodValue, MOOD_EMOJIS, parseMoodTags } from "@journal/shared";
import type { JournalEntry } from "@journal/powersync";
import { usePhotosByEntryQuery } from "@journal/shared/solid";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { ENTRY_ROW_SWIPE } from "@/lib/gestures/presets";
import { formatDayShort, formatEntryTime } from "../format";
import { PhotoGrid } from "./photo-grid";
import { useEntryMutations } from "../hooks/use-entry-mutations";
import { useEntrySwipeGestures } from "../hooks/use-entry-swipe-gestures";
import { useJournalData } from "../hooks/use-journal-data";

interface EntryCardProps {
  entry: JournalEntry;
  /** Shows the date (views that mix days: favorites, search…). */
  showDate?: boolean;
  /** Resolved entry tags (name). */
  tags?: () => { tag_id: string; name: string | null }[];
  /** Highlights this term in the body (search). */
  highlightTerm?: string;
}

const BODY_CLAMP_CLASS = "line-clamp-6";

function HighlightedBody(props: { text: string; term?: string }) {
  const parts = createMemo(() => {
    const term = props.term?.trim().toLowerCase();
    if (!term) {
      return null;
    }
    const lower = props.text.toLowerCase();
    const index = lower.indexOf(term);
    if (index === -1) {
      return null;
    }
    return {
      before: props.text.slice(0, index),
      match: props.text.slice(index, index + term.length),
      after: props.text.slice(index + term.length),
    };
  });

  return (
    <Show when={parts()} fallback={<>{props.text}</>}>
      {(p) => (
        <>
          {p().before}
          <mark class="rounded bg-primary/20 px-0.5 text-foreground">{p().match}</mark>
          {p().after}
        </>
      )}
    </Show>
  );
}

/** Journal entry card (day lists, favorites, search). */
export function EntryCard(props: EntryCardProps) {
  const { t, locale } = useI18n();
  const { db, drizzle, staleTime } = useJournalData();
  const { setFavorite, trashEntry } = useEntryMutations();
  const [confirmOpen, setConfirmOpen] = createSignal(false);

  const photos = usePhotosByEntryQuery({
    db,
    drizzle,
    staleTime,
    entryId: () => props.entry.id,
  });

  const moodEmoji = () => (isMoodValue(props.entry.mood) ? MOOD_EMOJIS[props.entry.mood] : null);
  const moodTags = () => parseMoodTags(props.entry.mood_tags);
  const isFavorite = () => props.entry.is_favorite === 1;

  const [surfaceRef, setSurfaceRef] = createSignal<HTMLElement>();
  const swipe = useEntrySwipeGestures({
    onToggleFavorite: () => {
      void setFavorite(props.entry.id, !isFavorite());
    },
    onRequestDelete: () => setConfirmOpen(true),
    getSurfaceRef: surfaceRef,
  });

  // A horizontal swipe captures the pointer; the title/body links must not also
  // navigate on the trailing click.
  const suppressClickAfterSwipe = (event: MouseEvent) => {
    if (swipe.getHasSwiped()) {
      event.preventDefault();
      swipe.resetHasSwiped();
    }
  };

  const cardTransform = () => `translate3d(${swipe.tx()}px, 0, 0)`;
  const cardTransition = () =>
    swipe.isAnimating() ? `transform ${ENTRY_ROW_SWIPE.cancelAnimationMs}ms ease-out` : "none";

  return (
    <div
      class="animate-fade-in relative overflow-hidden rounded-2xl"
      data-testid="entry-card-swipe"
    >
      {/* Action hints revealed under the card as it slides. */}
      <div class="pointer-events-none absolute inset-0 flex items-center justify-between px-5">
        <span class="flex items-center gap-1.5 text-warning" aria-hidden="true">
          <Star class="h-5 w-5 fill-current" />
        </span>
        <span class="flex items-center gap-1.5 text-destructive" aria-hidden="true">
          <Trash2 class="h-5 w-5" />
        </span>
      </div>
      <article
        ref={setSurfaceRef}
        class="group relative flex flex-col gap-2.5 rounded-2xl border border-border/40 bg-card/60 p-4 shadow-sm will-change-transform hover:shadow-md"
        style={{
          "touch-action": "pan-y",
          transform: cardTransform(),
          transition: cardTransition(),
        }}
        data-testid="entry-card"
        {...swipe.handlers}
      >
        <header class="flex items-center gap-2">
          <Link
            to="/entry/$entryId"
            params={{ entryId: props.entry.id }}
            class="flex-1 truncate text-left"
            onClick={suppressClickAfterSwipe}
          >
            <Show
              when={props.entry.title}
              fallback={
                <span class="text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
                  {props.showDate
                    ? formatDayShort(props.entry.entry_date ?? "", locale())
                    : formatEntryTime(props.entry.created_at, locale())}
                </span>
              }
            >
              <span
                class="truncate font-display text-[1.05rem] font-semibold text-foreground"
                data-testid="entry-title"
              >
                {props.entry.title}
              </span>
            </Show>
          </Link>

          <Show when={moodEmoji()}>
            <span class="text-base" title={`${props.entry.mood}/5`} data-testid="entry-mood">
              {moodEmoji()}
            </span>
          </Show>

          <button
            type="button"
            data-testid="entry-favorite"
            class={cn(
              "flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/50 transition-colors hover:bg-muted hover:text-foreground",
              isFavorite() && "text-warning hover:text-warning",
            )}
            aria-label={isFavorite() ? t(msg`Remove from favorites`) : t(msg`Add to favorites`)}
            aria-pressed={isFavorite()}
            onClick={() => {
              void setFavorite(props.entry.id, !isFavorite());
            }}
          >
            <Star class={cn("h-4 w-4", isFavorite() && "fill-current")} />
          </button>

          <button
            type="button"
            data-testid="entry-delete"
            class="hidden h-7 w-7 items-center justify-center rounded-full text-muted-foreground/50 transition-colors hover:bg-destructive/10 hover:text-destructive group-hover:flex"
            aria-label={t(msg`Delete entry`)}
            onClick={() => setConfirmOpen(true)}
          >
            <Trash2 class="h-4 w-4" />
          </button>
        </header>

        <Show when={props.entry.title && props.showDate}>
          <span class="text-[11px] text-muted-foreground/60">
            {formatDayShort(props.entry.entry_date ?? "", locale())}
          </span>
        </Show>

        <Link
          to="/entry/$entryId"
          params={{ entryId: props.entry.id }}
          class="text-left"
          onClick={suppressClickAfterSwipe}
        >
          <p
            class={cn(
              "whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-foreground/90",
              BODY_CLAMP_CLASS,
            )}
            data-testid="entry-body"
          >
            <HighlightedBody text={props.entry.body ?? ""} term={props.highlightTerm} />
          </p>
        </Link>

        <Show when={moodTags().length > 0 || (props.tags?.() ?? []).length > 0}>
          <div class="flex flex-wrap items-center gap-1.5">
            <For each={props.tags?.() ?? []}>
              {(tag) => (
                <span class="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                  #{tag.name}
                </span>
              )}
            </For>
            <For each={moodTags()}>
              {(tag) => (
                <span class="rounded-full bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                  {tag}
                </span>
              )}
            </For>
          </div>
        </Show>

        <PhotoGrid photos={() => photos.data ?? []} />
      </article>

      <ConfirmDialog
        title={t(msg`Delete this entry?`)}
        description={t(msg`The entry will be moved to the trash. You can restore it from there.`)}
        variant="destructive"
        open={confirmOpen()}
        onOpenChange={setConfirmOpen}
        onConfirm={() => {
          void trashEntry(props.entry.id);
        }}
      />
    </div>
  );
}
